import { Box, Chip, Dialog, DialogContent, DialogTitle, Drawer, Grid, Menu, Skeleton, MenuItem, Typography, ListSubheader } from "@mui/material";
import { useSettings } from "../contexts/SettingsContext";
import { DataGridPro, GridPagination } from "@mui/x-data-grid-pro";
import { dataGridStyle, colors } from "../theme";
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { securityColumns } from "./columns";
import LogSidePanel from "./LogSidePanel";
import { TiresiaRowBadge, lookupTiresiaRow, useTiresiaIpSeverity, useTiresiaAnomalousIps } from "./TiresiaInsight";
import { Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomToolbarWithButton from "./CustomToolBar";
import API from "../api";
import BlacklistRuleDialog from "./BlacklistRuleDialog";
import { isInternalIp, inventoryIpUrl } from "./ImportantFunctions";
import IntelligenceTimeFilter from "./IntelligenceTimeFilter";
import { startToast, updateToastError, updateToastWarning, updateToastSuccess } from "../utils/notification-loading";
import ClassificationCard from "./classificationCard";
import CloseIcon from "@mui/icons-material/Close";
import PanToolIcon from "@mui/icons-material/PanTool";
import { SuppressionSetting } from "./suppressionSettings";
import SuppressionView from "./suppressionView";
import ChooseIpDialog from "./ChooseIpSuppDialog";
import SuppressionSuggestions from "./SuppressionSuggestions";
import SearchIcon from '@mui/icons-material/Search';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NotesIcon from '@mui/icons-material/Notes';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import BlockIcon from '@mui/icons-material/Block';

const CappedFooter = ({ isCapped }) => (
  <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
    {isCapped && (
      <Chip
        label="5,000+ rows"
        size="small"
        sx={{
          fontSize: 11,
          height: 22,
          bgcolor: "rgba(255,165,0,0.15)",
          color: "#FFA500",
          border: "1px solid rgba(255,165,0,0.4)",
          mr: 1,
        }}
      />
    )}
    <Box sx={{ flex: 1 }}>
      <GridPagination />
    </Box>
  </Box>
);

const SecurityDataGrid = ({
  reseller = false,
  tipo,
  tableKey,
  passedPayload,
  passedFilter,    // ← array di bracket filter pre-costruiti
  filterKey = 0,   // ← quando cambia, resetta il filterModel (senza rimontare)
  setMessage,
  setCoinvIp,
  setContent,
  suppDialog,
  setSuppDialog,
  setViewExplain
}) => {
  const colKey = tableKey ?? String(tipo);
  const DEFAULT_COL_VIS = {};
  const { settings, updateSettings } = useSettings();

  const [rows, setRows] = useState([]);
  const [classificationData, setClassificationData] = useState([]);
  const timezone = sessionStorage.getItem("timezone") || "Etc/GMT";
  const [names, setNames] = useState("dns");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(DEFAULT_COL_VIS);

  // Sync once when settings are loaded from the server
  useEffect(() => {
    const saved = settings?.colVis?.[colKey];
    if (saved) setColumnVisibilityModel(saved);
  }, [settings, colKey]);
  const [charged, setCharged] = useState(true);
  const tipoLecs = sessionStorage.getItem("tipoLecs");
  const [suppData, setSuppData] = useState({});
  const [rowCounter, setRowCounter] = useState(0);
  const [isCapped, setIsCapped] = useState(false);
  const [hideBackup, setHideBackup] = useState(true);
  const [hideSuppressed, setHideSuppressed] = useState((tipo === 2 || tipo === 3) ? true : false);
  // Su High/Critical "suppressed" (risposta soppressa da regola) e "disabled" (risposta
  // disabilitata, es. Probe Mode) sono filtri indipendenti: servono per analizzare
  // separatamente i due insiemi in fase di tuning.
  const [hideDisabled, setHideDisabled] = useState(false);
  const [hideNotHandled, setHideNotHandled] = useState(false);
  const [hideHandled, setHideHandled] = useState(false);
  const [hideAcked, setHideAcked] = useState(false);
  const [payload, setPayload] = useState({ "tipo": passedPayload.tipo });
  const [subnetValue, setSubnetValue] = useState("");
  const [filterModel, setFilterModel] = useState([]);
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [isPrivateSource] = useState(passedPayload.isPrivateSource);
  const [isPrivateDestination] = useState(passedPayload.isPrivateDestination);
  const [ctx, setCtx] = useState(null);
  const [ipMenu, setIpMenu] = useState(null); // { mouseX, mouseY, ip }
  const [logPanelRow, setLogPanelRow] = useState(null);
  // true quando il drawer è aperto dal menu contestuale "Explain": la spiegazione parte subito.
  const [autoExplain, setAutoExplain] = useState(false);
  // Badge Tires-IA sulle righe con IP che hanno anomalie comportamentali (24h).
  const showTiresia = tipo >= 0 && tipo < 4;
  const { byIp: tiresiaByIp, loading: tiresiaLoading } = useTiresiaIpSeverity(showTiresia, rows);
  // Filtro rapido: solo eventi di host con anomalie Tires-IA. La lista IP (null = in caricamento) va al server.
  const [onlyTiresia, setOnlyTiresia] = useState(false);
  const tiresiaIps = useTiresiaAnomalousIps(showTiresia && onlyTiresia);
  const [groupLoading, setGroupLoading] = useState(false);
  const [quickSuppDialog, setQuickSuppDialog] = useState(false);
  const [quickSuppRow, setQuickSuppRow] = useState(null);
  // Solo High/Critical/Alert/Movements: crea una regola blacklist dagli IP del log (serve il modulo "blacklist" in licenza).
  const [blacklistDialog, setBlacklistDialog] = useState(false);
  const [blacklistRow, setBlacklistRow] = useState(null);
  const [blacklistEnabled, setBlacklistEnabled] = useState(false);
  // Log più rari delle ultime 24h (la stessa lista di Highlights → "Rarest logs", per signature): chip RARE sulla riga.
  const [rareSignatures, setRareSignatures] = useState(null);
  useEffect(() => {
    if (!(tipo >= 0 && tipo < 4)) return undefined;
    const ctrl = new AbortController();
    API.getLogsByDettaglio({ multiView: false })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setRareSignatures(new Set((Array.isArray(res.data) ? res.data : []).map((x) => x?.dettaglio).filter(Boolean)));
      })
      // se fallisce, il filtro Tires-IA resta valido con i soli host anomali
      .catch(() => { if (!ctrl.signal.aborted) setRareSignatures(new Set()); });
    return () => ctrl.abort();
  }, [tipo]);
  useEffect(() => {
    if (!(tipo >= 0 && tipo < 4)) return undefined;
    let cancelled = false;
    API.getLicense()
      .then((res) => {
        const mod = (res.data?.modules ?? []).find((m) => m.name === "blacklist");
        if (!cancelled) setBlacklistEnabled(!!mod?.enabled);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tipo]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Top/bottom IP per numero di log: a richiesta in un drawer, così la tabella usa tutta la larghezza.
  const [groupingOpen, setGroupingOpen] = useState(false);
  const [suggestRefresh, setSuggestRefresh] = useState(0);
  // Dopo un ack massivo (o il suo annullamento) la tabella va ricaricata: righe acked e "hide acked".
  const [ackRefresh, setAckRefresh] = useState(0);
  // Solo Alert/Movements: su High/Critical la soppressione è una regola di risposta.
  const canSuggest = (tipo === 2 || tipo === 3);

  // Quando si chiude il dialog di creazione regola aperto da un suggerimento, i
  // suggerimenti si ricaricano così la regola appena creata non viene riproposta.
  const ruleDialogOpen = Boolean(suppDialog);
  const prevRuleDialogOpen = useRef(ruleDialogOpen);
  useEffect(() => {
    if (prevRuleDialogOpen.current && !ruleDialogOpen && suggestOpen) setSuggestRefresh((n) => n + 1);
    prevRuleDialogOpen.current = ruleDialogOpen;
  }, [ruleDialogOpen, suggestOpen]);

  // Pattern rumorosi nascosti dalla vista senza regola ("Hide noise"), salvati per
  // utente e per sonda. Si inviano al server solo se attivi, così contatore e
  // paginazione restano coerenti con quello che si vede.
  const [noise, setNoise] = useState({ enabled: false, patterns: [] });
  useEffect(() => {
    if (!canSuggest) return undefined;
    const ctrl = new AbortController();
    API.getNoiseFilters({ tipo }, ctrl.signal)
      .then((res) => setNoise({ enabled: !!res.data?.enabled, patterns: res.data?.patterns ?? [] }))
      .catch(() => {});
    return () => ctrl.abort();
  }, [canSuggest, tipo]);

  const changeNoise = (next) => {
    setNoise(next);
    API.saveNoiseFilters({ tipo, ...next }).catch(() => updateToastError("Could not save the hidden noise patterns"));
  };

  const noiseRequest = useMemo(() => (
    canSuggest && noise.enabled && noise.patterns.length
      ? noise.patterns.map(({ signature, src_ip, dest_ip }) => ({ signature, src_ip, dest_ip }))
      : null
  ), [canSuggest, noise]);

  const handleCreateRuleFromSuggestion = (s) => {
    setSuppData({ ipSource: s.src_ip, ipDestination: s.dest_ip, dettaglio: s.signature });
    setSuppDialog(true);
  };
  const [timeFilter, setTimeFilter] = useState({
    dateFrom: passedPayload.dateFrom,
    dateTo: passedPayload.dateTo
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const safePassedFilter = Array.isArray(passedFilter) ? passedFilter : [];
  
  //set initial filter
  useEffect(() => {

    // Se viene passato un filtro pre-costruito (bracket), usalo direttamente
    if (safePassedFilter.length > 0) {
      setFilterModel(safePassedFilter);
      return;
    }

    if (passedPayload.ipMap && passedPayload.ipMap === true) {
      setFilterModel([
        {
          field: "ipSource",
          operator: "equals",
          value: passedPayload.source,
        },
        {
          logic: "or",
          field: "ipDestination",
          operator: "equals",
          value: passedPayload.destination,
        }
      ])
    } else {
      let transformedFilter = [
        ...(passedPayload.exact_detail
          ? [
            {
              logic: "and",
              field: "dettaglio",
              operator: "equals",
              value: passedPayload.exact_detail,
            },
          ]
          : []),
        ...(passedPayload.ipSource
          ? [
            {
              logic: "and",
              field: "ipSource",
              operator: "contains",
              value: passedPayload.ipSource,
            },
          ]
          : []),
        ...(passedPayload.ipDestination
          ? [
            {
              logic: "and",
              field: "ipDestination",
              operator: "contains",
              value: passedPayload.ipDestination,
            },
          ]
          : []),
      ];
      if (transformedFilter[0]?.logic) {
        delete transformedFilter[0].logic;
      }
      setFilterModel(transformedFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs once on mount to seed the initial filter from the passed-in payload/filter; not meant to re-run on every passedPayload/safePassedFilter change
  }, []);

  //change filters
  useEffect(() => {
    if (filterModel.length > 0) {
      setPayload({
        ...payload,
        filter: filterModel,
      })
    } else {
      const { filter, ...rest } = payload;
      setPayload(rest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payload intentionally omitted: it's read via spread to build the next payload, and adding it would re-fire this effect on every setPayload call (infinite loop)
  }, [filterModel]);

  useEffect(() => {
    setPayload({
      ...payload,
      source: source,
      destination: destination,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payload intentionally omitted: it's read via spread to build the next payload, and adding it would re-fire this effect on every setPayload call (infinite loop)
  }, [source, destination]);

  //change timeFilter
  useEffect(() => {
    if (timeFilter.dateFrom && timeFilter.dateTo && tipo !== -2) {
      const dateFrom = timeFilter.dateFrom.utc().format("YYYY-MM-DD HH:mm");
      const dateTo = timeFilter.dateTo.utc().format("YYYY-MM-DD HH:mm");
      setPayload({
        ...payload,
        timeFilter: [dateFrom, dateTo]
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- payload intentionally omitted: it's read via spread to build the next payload, and adding it would re-fire this effect on every setPayload call (infinite loop)
  }, [timeFilter]);

  // Parametri della query sui log, senza paginazione: condivisi da tabella e raggruppamenti.
  const logQuery = useMemo(() => ({
    ...payload,
    timezone: timezone,
    ...(tipo !== 0 && tipo !== 1
      ? { hideSuppressed: hideSuppressed }
      : { hideResponseSuppressed: hideSuppressed, hideDisabled: hideDisabled, hideNotHandled: hideNotHandled, hideHandled: hideHandled }),
    hideBackup: hideBackup,
    hideAcked: hideAcked,
    ...(noiseRequest ? { noisePatterns: noiseRequest } : {}),
    ...(subnetValue ? { subnet: subnetValue } : {}),
    ...(onlyTiresia && tiresiaIps ? { tiresiaIps, tiresiaSignatures: rareSignatures ? [...rareSignatures] : [] } : {}),
  }), [payload, timezone, tipo, hideSuppressed, hideDisabled, hideNotHandled, hideHandled, hideBackup, hideAcked, noiseRequest, subnetValue, onlyTiresia, tiresiaIps, rareSignatures]);

  // Top/bottom IP per numero di log: solo a drawer aperto, è l'aggregazione più pesante.
  useEffect(() => {
    if (!groupingOpen || tipo === -2 || !logQuery.timeFilter) return undefined;
    if (onlyTiresia && (tiresiaIps === null || rareSignatures === null)) return undefined;
    const ctrl = new AbortController();
    setGroupLoading(true);
    API.getSecurityLog({ ...logQuery, pageNumber: 0, limit: 1, group: true }, ctrl.signal)
      .then((res) => setClassificationData(res.data?.groupedData ?? []))
      .catch(() => {})
      .finally(() => { if (!ctrl.signal.aborted) setGroupLoading(false); });
    return () => ctrl.abort();
  }, [groupingOpen, logQuery, tipo, onlyTiresia, tiresiaIps, rareSignatures]);

  //API
  useEffect(() => {
    const abortCtrl = new AbortController();
    const signal = abortCtrl.signal;
    setCharged(true);
    if (tipo === -2) {
      API.getDetailCouple({
        ...passedPayload,
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }, signal).then((res) => {
        const newData = res.data.data.map((item, index) => ({
          id: index,
          ...item,
        }));
        setRows(newData);
        setRowCounter(res.data.totalLogs);
        setCharged(false);
      }).catch((err) => {
        // Una richiesta abortita (navigazione/cambio filtro prima che
        // arrivi la risposta) non deve spegnere lo skeleton: altrimenti
        // la griglia mostra per un istante "No data" con charged=false
        // e rows ancora vuote, prima che la richiesta reale finisca.
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        setCharged(false);
      });
    } else if (payload.timeFilter && !(onlyTiresia && (tiresiaIps === null || rareSignatures === null))) {
      API.getSecurityLog({
        ...logQuery,
        pageNumber: parseInt(paginationModel.page),
        limit: paginationModel.pageSize,
      }, signal).then((res) => {
        /*const newData = res.data.data.map((item, index) => ({
          id: index,
          ...item,
          response: tipo === 1 && !tipoLecs.includes("CORE") ?
            item.suppressed || item.disabled ? "Disabled" : "Procedural and Physical"
            : tipo === 0 || tipo === 1 ?
              item.suppressed || item.disabled ? "Disabled" : "Procedural"
              : undefined,
        }));*/

        const tipoLecsSafe = typeof tipoLecs === "string" ? tipoLecs : (tipoLecs || "");

        const newData = (Array.isArray(res?.data?.data) ? res.data.data : []).map((item, index) => {
          let response;
          if (item?.suppressed) {
            response = "Suppressed";
          }
          // response_handled=false arriva dal pipeline per eventi (es. conduit non
          // permesso high/critical) per cui non esiste una risposta automatica
          // gestita -- prevale sulla classificazione generica Procedural/CORE
          // sotto, che presume sempre una gestione esistente.
          else if (item?.response_handled === false) {
            response = "Not handled";
          }
          else {
            if (tipo === 1 && !tipoLecsSafe.includes("CORE")) {
              response = item?.disabled ? "Disabled" : "Procedural and Physical";
            } else if (tipo === 0 || tipo === 1) {
              response = item?.disabled ? "Disabled" : "Procedural";
            } else {
              response = undefined;
            }
          }


          return {
            // ✅ ID stabile: preferisci un id del backend se esiste
            // Se hai _id (Mongo) o idLog ecc., usa quello.
            id: item?._id ?? item?.id ?? `${paginationModel?.page ?? 0}-${index}`,
            ...item,
            response,
          };
        });


        setRows(newData);
        setRowCounter(res.data.totalLogs);
        setIsCapped(!!res.data.isCapped);
        // Un backend non aggiornato ignora noisePatterns: meglio dirlo che mostrare
        // un filtro "attivo" che non toglie niente.
        if (noiseRequest && !res.data.noiseApplied) {
          updateToastWarning("The server did not apply the hidden noise patterns. The backend may need to be updated or restarted.");
        }
        setCharged(false);
      }).catch((err) => {
        // Stesso motivo del ramo tipo === -2 sopra: non spegnere lo
        // skeleton per una richiesta abortita, solo per quella reale.
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        setCharged(false);
        const status = err?.response?.status;
        if (status === 408) {
          updateToastWarning("Connection to host timed out retry");
        } else {
          updateToastError("Operation failed");
        }
      });
    }
    return () => abortCtrl.abort();
  }, [payload, logQuery, paginationModel.page, paginationModel.pageSize, ackRefresh, noiseRequest, passedPayload, names, tipo, tipoLecs, onlyTiresia, tiresiaIps, rareSignatures]);

  const handleIpMenuOpen = (ip, event) => {
    if (!ip) return;
    setIpMenu({ mouseX: event.clientX, mouseY: event.clientY, ip });
  };


  const handleAck = async (row, shouldAck) => {
    if (!row.event_id) return;
    try {
      await API.setAckEvent({
        event_id: row.event_id,
        set: shouldAck ? 1 : 0,
        timestamp: row.timestamp,
        severity: row.severity,
        type: "security"
      });
      setRows((prev) =>
        prev.map((r) => r.id === row.id ? { ...r, acked: shouldAck } : r)
      );
    } catch {
      // silently fail — the button will revert on next data load
    }
  };

  const handlePaginationModelChange = (e) => {
    const { page } = e;
    if (page > paginationModel.page)
      setPaginationModel({
        ...paginationModel,
        page: paginationModel.page + 1,
      });
    else if (page < paginationModel.page)
      setPaginationModel({
        ...paginationModel,
        page: paginationModel.page - 1,
      });
  };

  return (
    <Box sx={tipo === -2 ? { height: "100%" } : { my: "20px" }}>
      <SuppDialog open={suppDialog} setOpen={setSuppDialog} tipo={tipo} suppData={suppData} />
      <QuickSuppDialog open={quickSuppDialog} setOpen={setQuickSuppDialog} row={quickSuppRow} tipo={tipo} />
      <BlacklistRuleDialog open={blacklistDialog} setOpen={setBlacklistDialog} row={blacklistRow} tipo={tipo} />
      {canSuggest && <SuppressionSuggestions open={suggestOpen} onClose={() => setSuggestOpen(false)} tipo={tipo} onCreateRule={handleCreateRuleFromSuggestion} refreshKey={suggestRefresh} noise={noise} onChangeNoise={changeNoise} onAcked={() => setAckRefresh((n) => n + 1)} />}
      <IpMenu ipMenu={ipMenu} setIpMenu={setIpMenu} reseller={reseller} />
      <ContextMenu
        ctx={ctx}
        setCtx={setCtx}
        filter={filterModel}
        setFilter={setFilterModel}
        onExplain={(row) => { setAutoExplain(true); setLogPanelRow(row); }}
        tipo={tipo}
        setSuppDialog={setSuppDialog}
        setSuppData={setSuppData}
        setQuickSuppDialog={setQuickSuppDialog}
        setQuickSuppRow={setQuickSuppRow}
        handleAck={handleAck}
        blacklistEnabled={blacklistEnabled}
        onBlacklist={(row) => { setBlacklistRow(row); setBlacklistDialog(true); }}
      />
      {/* <HeatMapDialog open={dialogHeatMap} setOpen={setDialogHeatMap} dettaglio={logHeatMap} /> */}
      <Grid container spacing={2} sx={tipo === -2 ? { height: "100%" } : undefined}>
        {tipo !== -2 &&
          <Grid size={12}>
            <IntelligenceTimeFilter
              showTitle={false}
              initialDateFrom={timeFilter.dateFrom}
              initialDateTo={timeFilter.dateTo}
              onDateChange={setTimeFilter}
            />
          </Grid>
        }
        <Grid size={12} sx={{ height: tipo === -2 ? "100%" : "calc(100vh - 270px)", minHeight: tipo === -2 ? undefined : 420 }}>
          <Box height={"100%"} sx={{ width: "111.11%", height: "111.11%", transform: "scale(0.9)", transformOrigin: "top left" }}>
            <DataGridPro
              sx={{
                ...dataGridStyle,
                "& .suppressedRow": {
                  borderLeft: "3px solid rgba(65, 177, 69, 0.6)",
                  backgroundColor: "rgba(43, 196, 48, 0.06)",
                },
                "& .disabledRow": {
                  borderLeft: "3px solid rgba(140, 155, 171, 0.6)",
                  backgroundColor: "rgba(140, 155, 171, 0.06)",
                },
              }}
              rows={rows}
              getRowId={(row) => row.id}
              localeText={{
                noRowsLabel: "No data in the set dates"
              }}
              columns={[
                {
                  field: "__info",
                  headerName: "",
                  width: 50,
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  hideable: false,
                  align: "center",
                  headerAlign: "center",
                  renderCell: (params) => (
                    <Tooltip title="View log details">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setAutoExplain(false); setLogPanelRow(params.row); }}
                        sx={{ color: colors.grey[100], "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" } }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  ),
                },
                ...(showTiresia ? [{
                  field: "__tiresia",
                  headerName: "",
                  width: 44,
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  hideable: false,
                  align: "center",
                  headerAlign: "center",
                  renderCell: (params) => tiresiaLoading
                    ? <Skeleton variant="rounded" width={24} height={24} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
                    : <TiresiaRowBadge info={lookupTiresiaRow(tiresiaByIp, params.row)} />,
                }] : []),
                ...securityColumns({
                  names,
                  timezone,
                  tipoLecs,
                  tipo,
                  reseller,
                  setMessage,
                  setCoinvIp,
                  setContent,
                  setViewExplain,
                  onAck: handleAck,
                  onIpClick: handleIpMenuOpen,
                  rareSignatures,
                }),
              ]}
              showToolbar={tipo !== -2}
              getRowHeight={() => "auto"}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(model) => {
                setColumnVisibilityModel(model);
                updateSettings("colVis", { ...settings?.colVis, [colKey]: model });
              }}
              rowBufferPx={50}
              pagination
              autoPageSize
              paginationMode="server"
              pageSizeOptions={[100]}
              rowCount={rowCounter}
              slots={{
                toolbar: () => (
                  <CustomToolbarWithButton
                    payload={payload}
                    rowCounter={rowCounter}
                    hideBackup={hideBackup}
                    setHideBackup={setHideBackup}
                    setHideSuppressed={setHideSuppressed}
                    hideSuppressed={hideSuppressed}
                    hideDisabled={hideDisabled}
                    setHideDisabled={setHideDisabled}
                    onOpenGrouping={tipo !== -2 ? () => setGroupingOpen(true) : undefined}
                    onToggleTiresia={showTiresia ? () => { setOnlyTiresia((v) => !v); setPaginationModel((p) => ({ ...p, page: 0 })); } : undefined}
                    tiresiaActive={onlyTiresia}
                    onOpenSuggestions={canSuggest ? () => setSuggestOpen(true) : undefined}
                    noiseCount={canSuggest ? noise.patterns.length : 0}
                    noiseEnabled={noise.enabled}
                    onToggleNoise={canSuggest ? () => changeNoise({ ...noise, enabled: !noise.enabled }) : undefined}
                    noisePatterns={noiseRequest}
                    hideNotHandled={hideNotHandled}
                    setHideNotHandled={setHideNotHandled}
                    hideHandled={hideHandled}
                    setHideHandled={setHideHandled}
                    hideAcked={hideAcked}
                    setHideAcked={setHideAcked}
                    setSubnetValue={setSubnetValue}
                    subnetValue={subnetValue}
                    filterModel={filterModel}
                    setFilterModel={setFilterModel}
                    source={source}
                    setSource={setSource}
                    destination={destination}
                    setDestination={setDestination}
                    isPrivateSource={isPrivateSource}
                    isPrivateDestination={isPrivateDestination}
                    setNames={setNames}
                    names={names}
                    type={tipo}
                  />
                ),
                footer: () => <CappedFooter isCapped={isCapped} />,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              paginationModel={paginationModel}
              getRowClassName={(params) =>
                params.row.backup ? "backupRow" : params.row.suppressed ? "suppressedRow" : params.row.disabled ? "disabledRow" : ""
              }
              loading={charged}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                },
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                },
                row: {
                  onContextMenu: (event) => {
                    const id = event.currentTarget.getAttribute("data-id");
                    const row = rows.find((r) => r.id === id);
                    event.preventDefault();
                    setCtx({ mouseX: event.clientX, mouseY: event.clientY, row: row });
                  },
                  style: { cursor: "context-menu" }
                }
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* ── Raggruppamenti per IP (a richiesta) ─────────────────────────── */}
      {tipo !== -2 && (
        <Drawer
          anchor="right"
          open={groupingOpen}
          onClose={() => setGroupingOpen(false)}
          slotProps={{ paper: { sx: { width: { xs: "100%", sm: 400 }, backgroundColor: "#1e1e1e", backgroundImage: "none", borderLeft: "1px solid #292929" } } }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25, backgroundColor: "#292929" }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Logs by IP</Typography>
            <IconButton size="small" onClick={() => setGroupingOpen(false)} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, p: 2 }}>
            <ClassificationCard data={classificationData} loading={groupLoading} />
          </Box>
        </Drawer>
      )}

      {/* ── Log details side panel ─────────────────────────────────────── */}
      <LogSidePanel
        log={logPanelRow}
        open={Boolean(logPanelRow)}
        onClose={() => setLogPanelRow(null)}
        explainable={tipo < 4}
        autoExplain={autoExplain}
      />
    </Box>
  );
}

const SuppDialog = ({ open, setOpen, tipo, suppData }) => {

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="md"
      slotProps={{ paper: { sx: { backgroundColor: "#141414", backgroundImage: "none", border: "1px solid #252525", borderRadius: 2.5 } } }}
    >
      <DialogTitle sx={{ borderBottom: "1px solid #1e1e1e", py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
          <PanToolIcon sx={{ color: "#449095", fontSize: 22 }} />
          <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
            {tipo === 0 || tipo === 1 ? "Manage Response" : "Suppress Log"}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: "20px !important", pb: 3 }}>
        {tipo === 0 || tipo === 1 ?
          <SuppressionSetting tipo={tipo} />
          :
          <SuppressionView suppData={suppData} />
        }
      </DialogContent>
    </Dialog>
  )
}

const IpMenu = ({ ipMenu, setIpMenu, reseller }) => {
  const navigate = useNavigate();

  const handleCopy = () => {
    startToast();
    const ip = String(ipMenu.ip);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(ip)
        .then(() => updateToastSuccess("IP copied to clipboard"))
        .catch(() => updateToastError("Failed to copy IP"));
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = ip;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      updateToastSuccess("IP copied to clipboard");
    }
    setIpMenu(null);
  };

  const handleView = () => {
    window.sessionStorage.setItem("selectedTab", "Inventory");
    setIpMenu(null);
    navigate(inventoryIpUrl(ipMenu.ip));
  };

  return (
    <Menu
      open={!!ipMenu}
      onClose={() => setIpMenu(null)}
      anchorReference="anchorPosition"
      anchorPosition={ipMenu ? { top: ipMenu.mouseY, left: ipMenu.mouseX } : undefined}
    >
      <MenuItem onClick={handleCopy}><ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />Copy IP</MenuItem>
      {ipMenu && isInternalIp(ipMenu.ip) && (
        <MenuItem onClick={handleView}><ManageSearchIcon fontSize="small" sx={{ mr: 1 }} />Host Details</MenuItem>
      )}
    </Menu>
  );
};

const ContextMenu = ({ ctx, setCtx, filter, setFilter, onExplain, tipo, setSuppDialog, setSuppData, setQuickSuppDialog, setQuickSuppRow, handleAck, blacklistEnabled, onBlacklist }) => {
  const style = {
    menu: {
      list: {
        sx: {
          maxWidth: 200,
          width: 200,
          py: 0,
          backgroundColor: "#393939",
          color: "#eaeaea",
          '& .MuiMenuItem-root': {
            py: 1,
            gap: 3,
          },
          " & .MuiListSubheader-root": {
            backgroundColor: "#2a2a2a",
            color: "#eaeaea",
            lineHeight: "40px",
          }
        }
      }
    },
  }

  function getNewFilter(field) {
    if (field === "source") {
      return {
        ...(filter.length > 0 && { logic: "and" }),
        field: "ipSource",
        operator: "equals",
        value: ctx.row.src_ip
      }
    } else if (field === "destination") {
      return {
        ...(filter.length > 0 && { logic: "and" }),
        field: "ipDestination",
        operator: "equals",
        value: ctx.row.dest_ip
      }
    } else if (field === "detail") {
      return {
        ...(filter.length > 0 && { logic: "and" }),
        field: "dettaglio",
        operator: "equals",
        value: ctx.row.signature
      }
    } else {
      return [
        {
          ...(filter.length > 0 && { logic: "and" }),
          field: "ipSource",
          operator: "equals",
          value: ctx.row.src_ip
        },
        {
          logic: "and",
          field: "ipDestination",
          operator: "equals",
          value: ctx.row.dest_ip
        },
        {
          logic: "and",
          field: "dettaglio",
          operator: "equals",
          value: ctx.row.signature
        }
      ]
    }
  }

  const handleFilter = (field) => {
    const newFilter = getNewFilter(field);
    if (Array.isArray(newFilter)) {
      setFilter((prev) => [...prev, ...newFilter]);
    } else {
      setFilter((prev) => [...prev, newFilter]);
    }
  }

  const handleExplain = () => {
    if (ctx?.row) onExplain(ctx.row);
    setCtx(null);
  }

  const handleSuppression = () => {
    // Nessuna risposta automatica gestita per questo evento (es. conduit non
    // permesso high/critical) -- non ha senso configurarne una manualmente.
    // Il MenuItem è già disabled per questo caso, questo è solo un secondo
    // livello di sicurezza.
    if (ctx?.row?.response_handled === false) { setCtx(null); return; }
    setSuppData({
      ipSource: ctx.row.src_ip,
      ipDestination: ctx.row.dest_ip,
      dettaglio: ctx.row.signature
    })
    setSuppDialog(true);
    setCtx(null);
  }

  const handleQuickSuppression = () => {
    if (ctx?.row?.response_handled === false) { setCtx(null); return; }
    setQuickSuppRow(ctx.row);
    setQuickSuppDialog(true);
    setCtx(null);
  }

  const handleBlacklist = () => {
    if (ctx?.row) onBlacklist(ctx.row);
    setCtx(null);
  }

  const handleAckFromMenu = () => {
    if (ctx?.row) handleAck(ctx.row, !ctx.row.acked);
    setCtx(null);
  }

  return (
    <Menu
      open={!!ctx}
      onClose={() => setCtx(null)}
      anchorReference="anchorPosition"
      anchorPosition={ctx ? { top: ctx.mouseY, left: ctx.mouseX } : undefined}
      slotProps={style.menu}
    >
      {(tipo === 0 || tipo === 1) &&
        <>
          <ListSubheader>Response</ListSubheader>
          <MenuItem
            onClick={handleQuickSuppression}
            disabled={ctx?.row?.response_handled === false}
            title={ctx?.row?.response_handled === false ? "This event has no managed/automated response" : undefined}
          >
            <PanToolIcon /> Quick Suppress
          </MenuItem>
          <MenuItem onClick={handleAckFromMenu}>
            {ctx?.row?.acked ? <HighlightOffIcon /> : <CheckCircleOutlineIcon />}
            {ctx?.row?.acked ? "Remove ACK" : "Mark as ACK'd"}
          </MenuItem>
          <MenuItem
            onClick={handleBlacklist}
            disabled={!blacklistEnabled}
            title={!blacklistEnabled ? "Blacklist module not licensed" : undefined}
          >
            <BlockIcon /> Add to Blacklist
          </MenuItem>
        </>
      }
      {(tipo === 2 || tipo === 3) &&
        <>
          <ListSubheader>Suppression</ListSubheader>
          <MenuItem
            onClick={handleSuppression}
            disabled={ctx?.row?.response_handled === false}
            title={ctx?.row?.response_handled === false ? "This event has no managed/automated response" : undefined}
          >
            <PanToolIcon /> Suppress
          </MenuItem>
          <MenuItem onClick={handleAckFromMenu}>
            {ctx?.row?.acked ? <HighlightOffIcon /> : <CheckCircleOutlineIcon />}
            {ctx?.row?.acked ? "Remove ACK" : "Mark as ACK'd"}
          </MenuItem>
          <MenuItem
            onClick={handleBlacklist}
            disabled={!blacklistEnabled}
            title={!blacklistEnabled ? "Blacklist module not licensed" : undefined}
          >
            <BlockIcon /> Add to Blacklist
          </MenuItem>
        </>
      }

      <ListSubheader>Filter By</ListSubheader>
      <MenuItem onClick={() => handleFilter("source")}><CallMadeIcon />IP Source</MenuItem>
      <MenuItem onClick={() => handleFilter("destination")}><CallReceivedIcon />IP Destination</MenuItem>
      <MenuItem onClick={() => handleFilter("detail")}><NotesIcon />Detail</MenuItem>
      <MenuItem onClick={() => handleFilter("all")}><ManageSearchIcon />All</MenuItem>

      {tipo < 4 && (
        <>
          <ListSubheader>Tiresia</ListSubheader>
          <MenuItem onClick={handleExplain}><SearchIcon /> Explain</MenuItem>
        </>
      )}
    </Menu>
  )
}

const QuickSuppDialog = ({ open, setOpen, row, tipo }) => {
  const handleAddRule = (srcIp, destIp) => {
    // Stesso guard del context menu -- il "tasto dentro" (Add Rule di
    // ChooseIpDialog) non deve poter impostare una risposta per un evento
    // senza risposta automatica gestita, anche se il dialog è stato aperto
    // per un'altra via.
    if (row?.response_handled === false) {
      updateToastError("This event has no managed/automated response");
      return;
    }
    startToast();
    API.manageSuppression({
      action: "add",
      src_ip: srcIp,
      dest_ip: destIp,
      dettaglio: row?.signature ?? "",
      severity: row?.severity ?? tipo,
    }).then(() => {
      updateToastSuccess("Rule Added Successfully");
      setOpen(false);
    }).catch(() => updateToastError("Error Adding Rule"));
  };

  return (
    <ChooseIpDialog
      open={open}
      setOpen={setOpen}
      handleAddRule={handleAddRule}
      initialSrcIp={row?.src_ip ?? "ANY"}
      initialDestIp={row?.dest_ip ?? "ANY"}
      dettaglio={row?.signature ?? null}
    />
  );
};

/* const HeatMapDialog = ({ open, setOpen, dettaglio }) => {

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="xl"
      sx={{
        "& .MuiDialog-paper": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
    }}>
      <DialogTitle sx={{ display: "flex", flexDirection: "row"}}>
        <Typography sx={{ fontSize: 20, fontWeight: 600, fontFamily: "Poppins"}}>Selected Detail:</Typography>&nbsp;
        <Typography sx={{ fontSize: 20, fontWeight: 600, fontFamily: "Poppins", color: "#459095"}}>{dettaglio}</Typography>
      </DialogTitle>
      <DialogContent>
        <HeatMapLog logDetail={[dettaglio]} />
      </DialogContent>
    </Dialog>
  );
} */

export default SecurityDataGrid;